import { useToast } from "@/hooks/use-toast";
import { BidDetailsForm } from "@/components/admin/bids/BidDetailsForm";
import { Position } from "@/services/positionService";
import { useEffect, useState } from "react";
import { AxiosResponse } from "axios";
import { EventStatusEnum } from "@/types/eventStatus";
import { useParams, useNavigate } from "react-router-dom";
import { axiosInstance } from "@/pages/auth/auth.context";
import { BidStatusUpdate } from "@/components/admin/bids/BidStatusUpdate";
import { Button } from "@/components/ui/button";

export default function BidDetails() {
  const { toast } = useToast();
  const [positions, setPositions] = useState<Position[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [currentStatus, setCurrentStatus] = useState<EventStatusEnum>(
    EventStatusEnum.NEW,
  );
  const { id } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    async function fetchPositions() {
      setLoading(true);
      try {
        const route = `${process.env.REACT_APP_BASE_API_URL}position`;
        const response: AxiosResponse<Position[]> =
          await axiosInstance.get(route);
        setPositions(response.data);
      } catch (error) {
        console.error("ERROR FETCHING POSITIONS", error);
        setError(error.message || "Unable to fetch positions.");
        toast({
          title: "Error",
          description: "Unable to fetch positions.",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    }

    fetchPositions();
  }, []);

  useEffect(() => {
    if (id) {
      fetchBidStatus();
    }
  }, [id]);

  const fetchBidStatus = async () => {
    try {
      const route = `${process.env.REACT_APP_BASE_API_URL}event/bid/${id}/status`;
      const response = await axiosInstance.get(route);
      if (response.data && response.data.status) {
        setCurrentStatus(response.data.status as EventStatusEnum);
      }
    } catch (error) {
      console.error("Error fetching bid status:", error);
      toast({
        title: "Error",
        description: "Failed to fetch bid status.",
        variant: "destructive",
      });
    }
  };

  const handleStatusUpdate = async (newStatus: EventStatusEnum) => {
    try {
      const route = `${process.env.REACT_APP_BASE_API_URL}event/bid/${id}/status`;
      await axiosInstance.put(route, null, {
        params: { status: newStatus },
      });
      setCurrentStatus(newStatus);
      toast({
        title: "Status Updated",
        description: `Bid status updated to ${newStatus}.`,
      });
    } catch (error) {
      console.error("Error updating bid status:", error);
      toast({
        title: "Error",
        description: "Failed to update bid status.",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="container max-w-2xl mx-auto py-10 px-4">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold text-foreground">Bid Details</h1>
        <div className="flex flex-col items-end gap-2">
          <BidStatusUpdate
            eventId={id}
            currentStatus={currentStatus}
            onStatusUpdate={handleStatusUpdate}
          />
          <div className="flex gap-2">
            {/* A bid and an event share an id, so the event journal is the
                same ledger an admin takes call notes in. */}
            <Button
              variant="outline"
              onClick={() => navigate(`/admin/events/${id}/journal`)}
            >
              Journal
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate(`/admin/bids/${id}/print`)}
            >
              Print Version
            </Button>
          </div>
        </div>
      </div>
      <BidDetailsForm positions={positions} eventId={id} />
    </div>
  );
}
